import { type D3ZoomEvent, zoom as d3Zoom, type ZoomBehavior, zoomIdentity } from 'd3-zoom';
import { active } from 'd3-transition';
import EventEmitter from 'eventemitter3';
import { defaultTo } from 'lodash-es';
import { getYearDays, oneDay } from './graph-helpers';
import { CursorFunction } from './cursor-functions';
import type { Graph } from './graph';

const defaultZoomTransitionDuration = 250; // ms

type Extent = [[number, number], [number, number]];

export class ZoomHelper extends EventEmitter {
    graph: Graph;

    maxZoom: number;

    zoomY: boolean;

    zoomTransitionDuration?: number;

    updateXAxis: boolean;

    extent?: Extent;

    translateExtent?: Extent;

    zoom: ZoomBehavior<Element, unknown>;

    resetZoomScale: number;

    constructor(
        graph: Graph,
        maxZoom: number,
        zoomY: boolean,
        restrict: boolean,
        zoomTransitionDuration?: number
    ) {
        super();

        this.graph = graph;
        this.maxZoom = maxZoom;
        this.zoomY = zoomY;
        this.zoomTransitionDuration = zoomTransitionDuration;
        this.updateXAxis = true;

        if (restrict) {
            this.extent = [
                [0, 0],
                [this.graph.graphWidth, this.graph.graphHeight],
            ];

            this.translateExtent = [
                [0, 0],
                [this.graph.graphWidth, this.graph.graphHeight],
            ];
        }

        // Zoom for the graph.
        this.zoom = d3Zoom().scaleExtent([1, this.maxZoom]);

        if (this.extent) {
            this.zoom.extent(this.extent);
        }

        if (this.translateExtent) {
            this.zoom.translateExtent(this.translateExtent);
        }

        this.setInitialZoomLevel();

        // Only allow zooming when we are in zoom mode.
        this.zoom.filter((event: MouseEvent) => {
            // Ignore right mouse clicks.
            if (event.button === 2) {
                return false;
            }

            return this.graph.cursorFunction$.getValue() === CursorFunction.ZOOM;
        });

        this.zoom.on('zoom.graph', this.onZoom.bind(this));

        this.graph.zoomElement.call(this.zoom);

        this.resetZoomScale = 1;
    }

    setInitialZoomLevel(): void {}

    onZoom(event: D3ZoomEvent<Element, unknown>): void {
        if (event.sourceEvent && (event.sourceEvent as { type: string }).type === 'brush') {
            // Ignore zoom-by-brush.
            return;
        }

        const t = event.transform;

        if (this.updateXAxis) {
            // Re-scale x and y axis during zoom.
            this.graph.xAxis.scale.domain(t.rescaleX(this.graph.xAxis.originalScale).domain());

            if (this.zoomY) {
                this.graph.yAxis.scale.domain(t.rescaleY(this.graph.yAxis.originalScale).domain());
            }
        }

        this.emit('zoomed', { inTransition: !!active(this.graph.zoomElement.node()) });
    }

    zoomTransition() {
        return this.graph.zoomElement
            .transition()
            .duration(defaultTo(this.zoomTransitionDuration, defaultZoomTransitionDuration))
            .on('end', () => {
                this.emit('transition-end');
            });
    }

    resetZoom(): void {
        this.zoom.scaleTo(this.zoomTransition(), this.resetZoomScale);
    }
}

export class ValueTimeGraphZoomHelper extends ZoomHelper {
    constructor(
        graph: Graph,
        maxZoom: number,
        zoomY: boolean,
        restrict: boolean,
        zoomTransitionDuration?: number
    ) {
        super(graph, maxZoom, zoomY, restrict, zoomTransitionDuration);

        this.zoom.on('end', () => {
            this.updateXAxis = true;
        });
    }

    setZoomToXAxis(): void {
        if (this.graph.animate) {
            this.updateXAxis = false;

            const min = this.graph.xAxis.getMin();
            const max = this.graph.xAxis.getMax();
            const diff = max - min;
            const scale = getYearDays() / (diff / oneDay);
            const identity = zoomIdentity
                .scale(scale)
                .translate(-this.graph.xAxis.originalScale(min), 0);

            this.zoom.transform(this.graph.zoomElement, identity);
        }
    }

    setInitialZoomLevel(): void {
        this.setZoomToXAxis();
        this.updateXAxis = true;
    }

    zoomToScale(days: number): void {
        this.resetZoomScale = getYearDays() / days;
        this.resetZoom();
    }
}
